import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { TextField } from "formik-material-ui";
import {
  Box,
  Button,
  Backdrop,
  Typography,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";
import Select from "react-select";
import countryList from "react-select-country-list";
import axios from "axios";
import { useDispatch } from "react-redux";
import { createUser } from "../actions/user";
import styles from "./RegistrationForm.module.css";

const useStyles = makeStyles((theme) => ({
  submitButton: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    "&:hover": {
      background: theme.palette.primary.main,
    },
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

const CHECK_USERNAME_URL =
  "https://admin.cloudflog.com/api/user/checkUsername/";

const checkAvailability = async (field, value) => {
  try {
    const res = await axios.post(CHECK_USERNAME_URL, { [field]: value });
    return res.data.msg === "Valid";
  } catch {
    return false;
  }
};

const buildValidationSchema = (showMobileInput) =>
  Yup.object().shape({
    firstName: Yup.string().required("First name is required"),
    lastName: Yup.string().required("Last name is required"),
    nickName: Yup.string().required("Nickname is required"),
    email: showMobileInput
      ? Yup.string().email("Enter a valid email")
      : Yup.string()
          .email("Enter a valid email")
          .required("Email is required"),
    mobileNo: showMobileInput
      ? Yup.string().required("Mobile number is required")
      : Yup.string(),
    countryCode: Yup.string().matches(/^\+[0-9]+$/, {
      message: "Country code must start with + followed by numbers",
      excludeEmptyString: true,
    }),
    country: Yup.string().required("Country is required"),
  });

const RegistrationForm = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const options = useMemo(() => countryList().getData(), []);

  const phoneNumber = localStorage.getItem("phoneNumber");
  const emailId = localStorage.getItem("email");
  const showMobileInput = !!emailId;

  const validationSchema = useMemo(
    () => buildValidationSchema(showMobileInput),
    [showMobileInput]
  );

  const initialValues = {
    firstName: "",
    lastName: "",
    nickName: "",
    email: emailId || "",
    mobileNo: phoneNumber || "",
    countryCode: "+852",
    country: "",
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setIsLoading(true);
    setError(null);

    const mobileNumber =
      !phoneNumber || phoneNumber === ""
        ? values.countryCode + values.mobileNo
        : values.mobileNo;

    if (!(await checkAvailability("nickName", values.nickName))) {
      setError("The Nickname is taken. Try another");
      setIsLoading(false);
      setSubmitting(false);
      return;
    }

    if (values.email && !(await checkAvailability("email", values.email))) {
      setError("The Email is taken. Try another");
      setIsLoading(false);
      setSubmitting(false);
      return;
    }

    if (
      mobileNumber &&
      !(await checkAvailability("mobile_number", mobileNumber))
    ) {
      setError("The Mobile Number is taken. Try another");
      setIsLoading(false);
      setSubmitting(false);
      return;
    }

    const formData = new FormData();
    formData.append("email", values.email);
    formData.append("password", "");
    formData.append("mobile_number", mobileNumber);
    formData.append("country", values.country);
    formData.append("first_name", values.firstName);
    formData.append("last_name", values.lastName);
    formData.append("nickName", values.nickName);
    formData.append("nameOfTwoDealers", "");
    formData.append("credibleReferences", "");
    formData.append("securityQues2Shop", "");
    formData.append("securityQues2Website", "");
    formData.append("securityQues2Company", "");
    formData.append("is_member_of_watchtrade", "");
    formData.append("is_on_other_platform", "");
    formData.append("trade_group_name", "");
    formData.append("other_platform_name", "");

    dispatch(createUser(formData, history, setError, setIsLoading));
    setSubmitting(false);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnChange
      validateOnBlur
    >
      {({ values, errors, touched, setFieldValue, submitForm }) => (
        <Form className={styles.formContainer}>
          <Field
            fullWidth
            name="firstName"
            label="First Name *"
            variant="standard"
            component={TextField}
            inputProps={{ className: styles.inputField }}
          />
          <Field
            fullWidth
            name="lastName"
            label="Last Name *"
            variant="standard"
            component={TextField}
            inputProps={{ className: styles.inputField }}
          />
          <Field
            fullWidth
            name="nickName"
            label="Nick Name *"
            variant="standard"
            component={TextField}
            inputProps={{ className: styles.inputField }}
          />

          {showMobileInput ? (
            <Box className={styles.mobileRow}>
              <Field
                name="countryCode"
                label="Country *"
                component={TextField}
                className={styles.countryCodeField}
                inputProps={{ className: styles.inputFieldSmall }}
              />
              <Field
                fullWidth
                name="mobileNo"
                label="Mobile *"
                variant="standard"
                component={TextField}
                className={styles.emailField}
                inputProps={{ className: styles.inputField }}
              />
            </Box>
          ) : (
            <Field
              fullWidth
              name="email"
              type="email"
              label="Email *"
              variant="standard"
              component={TextField}
              className={styles.emailField}
              inputProps={{ className: styles.inputField }}
            />
          )}

          <Typography className={styles.countryLabel}>
            Select Country *
          </Typography>
          <Select
            options={options}
            placeholder="Select Country"
            aria-label="country"
            onChange={(opt) => setFieldValue("country", opt.value)}
          />
          {touched.country && errors.country && (
            <Typography className={styles.errorText}>
              {errors.country}
            </Typography>
          )}

          <Typography className={styles.credentialNote}>
            We may contact you in future to request additional information or
            check your credentials.
          </Typography>

          <Typography className={styles.termsText}>
            By creating an account, you agree to our{" "}
            <a
              href="https://www.cloudflog.com/terms-of-use"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              href="https://www.cloudflog.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
            .
          </Typography>

          {error && (
            <Typography className={styles.errorText}>{error}</Typography>
          )}

          <Button
            variant="contained"
            fullWidth
            className={`${styles.submitButton} ${classes.submitButton}`}
            disabled={isLoading}
            onClick={submitForm}
          >
            Let's Go
          </Button>

          <Backdrop className={classes.backdrop} open={isLoading}>
            <CircularProgress color="inherit" />
          </Backdrop>
        </Form>
      )}
    </Formik>
  );
};

export default RegistrationForm;
